import * as React from "react";
import { Pencil } from "lucide-react";
import { cn } from "../../lib/utils";

type InlineTextProps = {
  as?: "h1" | "span";
  label: string;
  editLabel: string;
  value: string;
  saved: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  editable: boolean;
  onChange: (value: string) => void;
  onCancel: () => void;
  /** Resolves true once saved. A rejected value keeps the field open. */
  onCommit: () => Promise<boolean>;
};

/** The text becomes its own field: Enter or leaving saves, Escape cancels. */
export function InlineText({
  as: Element = "span",
  label,
  editLabel,
  value,
  saved,
  placeholder,
  required,
  disabled,
  editable,
  onChange,
  onCancel,
  onCommit,
}: InlineTextProps) {
  // A recovered draft reopens the field rather than looking already saved.
  const [editing, setEditing] = React.useState(value !== saved);
  const settled = React.useRef(false);
  const editButton = React.useRef<HTMLButtonElement>(null);
  const returnFocus = React.useRef(false);
  React.useEffect(() => {
    if (!editing && returnFocus.current) {
      returnFocus.current = false;
      editButton.current?.focus();
    }
  }, [editing]);
  const open = () => {
    settled.current = false;
    setEditing(true);
  };
  const cancel = () => {
    settled.current = true;
    onCancel();
    setEditing(false);
  };
  const commit = async () => {
    if (settled.current) return;
    if (value === saved || (required && !value.trim())) {
      cancel();
      return;
    }
    settled.current = true;
    if (await onCommit()) setEditing(false);
    else settled.current = false;
  };
  if (editing)
    return (
      <Element className="vl-inline-text">
        <input
          className="vl-input vl-inline-input"
          style={
            {
              "--vl-inline-width": `${Math.max(value.length + 2, 16)}ch`,
            } as React.CSSProperties
          }
          aria-label={label}
          autoFocus
          value={value}
          required={required}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              returnFocus.current = true;
              void commit();
            }
            if (event.key === "Escape") {
              event.preventDefault();
              returnFocus.current = true;
              cancel();
            }
          }}
        />
      </Element>
    );
  return (
    <span className="vl-inline-text" data-editable={editable || undefined}>
      <Element
        className={cn(
          "vl-inline-value",
          !value && placeholder && "vl-inline-placeholder",
        )}
        onClick={editable ? open : undefined}
      >
        {value || placeholder}
      </Element>
      {editable && (
        <button
          ref={editButton}
          type="button"
          className="vl-inline-edit"
          aria-label={editLabel}
          title={editLabel}
          onClick={open}
        >
          <Pencil aria-hidden="true" />
        </button>
      )}
    </span>
  );
}
